import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { ADJUSTMENT } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialSearchValues = {
  SearchText: "",
  TransactionNo: "",
  ReferenceNo: "",
  BranchId: null,
  DepartmentId: null,
  TransactionDateFrom: null,
  TransactionDateTo: null,
  PageNumber: 1,
  PageSize: 10,
};

const columns = [
  {
    title: "Transaction No",
    dataIndex: "TransactionNo",
    key: "TransactionNo",
    width: 150,
  },
  {
    title: "Transaction Date",
    dataIndex: "TransactionDate",
    key: "TransactionDate",
    width: 150,
    render: (text) => {
      if (!text) return "";
      return text.split("T")[0];
    },
  },
  {
    title: "Branch",
    dataIndex: "BranchName",
    key: "BranchName",
    width: 150,
  },
  {
    title: "Department",
    dataIndex: "DepartmentName",
    key: "DepartmentName",
    width: 150,
  },
  {
    title: "Status",
    dataIndex: "IsSubmit",
    key: "IsSubmit",
    width: 100,
    render: (text) => (text ? "Post" : "Un-Post"),
  },
];

const ADJUSTMENT_REPORT_COLUMNS = [
  { key: "ItemName", label: "Item" },
  { key: "IssuanceUnit", label: "Unit" },
  { key: "StockIU", label: "Stock", numeric: true },
  { key: "QtyIU", label: "Qty", numeric: true },
  { key: "InOut", label: "In/Out" },
];

const Adjustment = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const componentRefPrint = useRef();
  const [reportData, setReportData] = useState(null);

  const [searchFields, setSearchFields] = useState(initialSearchValues);
  const [transactionList, setTransactionList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [tableColumns] = useState([...columns]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, []);

  const fetchData = async (searchParams = {}, pageNumber = 1, pageSize = 10) => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: ADJUSTMENT,
      SearchText: searchParams.SearchText || "",
      TransactionNo: searchParams.TransactionNo || "",
      ReferenceNo: searchParams.ReferenceNo || "",
      BranchId: searchParams.BranchId || null,
      DepartmentId: searchParams.DepartmentId || null,
      TransactionDate: searchParams.TransactionDateFrom || null,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;
      setTransactionList(dataSet.Data || dataSet.DataSet?.Table || []);

      const totalCount =
        dataSet?.TotalCount ||
        dataSet?.TotalRecords ||
        dataSet?.Data?.length ||
        0;
      setPagination((prev) => ({
        ...prev,
        current: pageNumber,
        pageSize: pageSize,
        total: totalCount,
      }));

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || [],
          Departments: dataSet.AdditionalData?.Departments || [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const handleSearchChange = (data) => {
    setSearchFields((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(searchFields, 1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchFields(initialSearchValues);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(initialSearchValues, 1, pagination.pageSize);
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));

    fetchData(searchFields, paginationInfo.current, paginationInfo.pageSize);
  };

  const handleDeleteRow = (id, record) => {
    if (!checkFeatureAuth("DELETE", "StockAdjustment")) {
      return;
    }
    const payload = {
      OperationId: 4,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: id,
    };

    postRequest("/Inv_Transaction", payload, controller)
      .then((response) => {
        if (response.error) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(
            response.data.DataSet?.Table?.errorMessage || "Error deleting transaction"
          );
          return;
        }
        message.success(response.successMessage || "Transaction deleted successfully");
        fetchData(searchFields, pagination.current, pagination.pageSize);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error deleting transaction:", error);
          message.error("Failed to delete transaction");
        }
      });
  };

  const handleEditRow = (setUpdateId) => { };

  const searchPanel = (
    <Fragment>
      <FormTextField
        colSpan={6}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={searchFields.TransactionNo}
        onChange={handleSearchChange}
      />

      <FormSelect
        colSpan={6}
        listItem={supportingTable.Branches || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={searchFields.BranchId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      <FormSelect
        colSpan={6}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={searchFields.DepartmentId}
        onChange={handleSearchChange}
        allowClear={true}
      />
    </Fragment>
  );

  return (
    <Fragment>
    <BasicFormComponentInvTransaction
      formTitle="Adjustment"
      searchPanel={searchPanel}
      searchSubmit={handleSearchSubmit}
      tableColumn={tableColumns}
      tableRows={transactionList}
      tableLoading={tableLoading}
      actionID="TransactionMasterId"
      editRow={handleEditRow}
      deleteRow={handleDeleteRow}
      hideDelete={!checkFeatureAuth("DELETE", "StockAdjustment", { showErrorMessage: false })}
      baseRoute="/stockadjustment"
      reset={false}
      handleReset={handleReset}
      paginationConfig={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
      }}
      onPaginationChange={handleTableChange}
      report={true}
      viewReport={viewReport}
      componentRefPrint={componentRefPrint}
    />
    <div style={{ display: "none" }}>
      <TestComponentToPrint
        ref={componentRefPrint}
        Bill={
          <InvTransactionReportContent
            title="Adjustment Detail Report"
            data={reportData}
            detailColumns={ADJUSTMENT_REPORT_COLUMNS}
          />
        }
      />
    </div>
  </Fragment>
  );
};

export default Adjustment;
